/** @file targetScreenOf: the one target-pixel -> screen mapping of the upscaled puppet target. */
import type { ViewportLayout } from './viewportLayout';

/**
 * Where the pixel-art target sits on screen: target point `(x, y)` (px, y down) lands on device pixel
 * `origin + scale (x, y)` of the full-resolution canvas and on CSS pixel `(origin + scale (x, y)) / dpr` of the
 * viewport box; `toCss` / `toTarget` are that map and its inverse.
 */
export type TargetScreen = {
  origin: [number, number]; scale: number; dpr: number;
  toCss(x: number, y: number): [number, number]; toTarget(cssX: number, cssY: number): [number, number];
};

/**
 * The layout's map: `origin = layout.offset` (whole device px, the target centred on the canvas), `scale = fit.scale`
 * (the integer upscale, device px per target px), `dpr = fit.dpr`. The overlay camera (`overlayFrame`), the CSS
 * labels (`cssPoint`) and the pointer (`targetPoint`, `cssToTarget`) all read it, so a target pixel is one screen spot.
 */
export function targetScreenOf(layout: ViewportLayout): TargetScreen {
  const [ox, oy] = layout.offset;
  const { scale, dpr } = layout.fit;
  return {
    origin: [ox, oy], scale, dpr,
    toCss: (x, y) => [(ox + x * scale) / dpr, (oy + y * scale) / dpr],
    toTarget: (cssX, cssY) => [(cssX * dpr - ox) / scale, (cssY * dpr - oy) / scale],
  };
}
